import * as React from "react";
import { TableCell } from "@mui/material";
import { DataTable, type DataTableColumn } from "@/components/dashboard/DataTable";
import { QuantityCell } from "@/components/dashboard/QuantityCell";
import { PriceCell } from "@/components/dashboard/PriceCell";
import { PositionBadge } from "@/components/dashboard/PositionBadge";

export interface OrderRow {
  execution_id: string;
  instrument_token: string;
  exchange?: string | null;
  order_type?: string | null;
  side: string;
  quantity: number;
  filled_quantity?: number | null;
  price?: number | null;
  status: string;
  created_time?: string | null;
  updated_time?: string | null;
}

interface OrderTableProps {
  orders: OrderRow[];
}

const notReported = <span style={{ fontStyle: "italic", opacity: 0.6 }}>Not reported by backend</span>;

export function OrderTable({ orders }: OrderTableProps): React.JSX.Element {
  const columns: Array<DataTableColumn<OrderRow>> = [
    {
      key: "order_id",
      label: "Order ID",
      sortable: true,
      sortValue: (row) => row.execution_id,
      render: (row) => <TableCell key="order_id">{row.execution_id}</TableCell>,
    },
    {
      key: "symbol",
      label: "Symbol",
      sortable: true,
      sortValue: (row) => row.instrument_token,
      render: (row) => <TableCell key="symbol">{row.instrument_token}</TableCell>,
    },
    {
      key: "exchange",
      label: "Exchange",
      render: (row) => <TableCell key="exchange">{row.exchange ?? notReported}</TableCell>,
    },
    {
      key: "order_type",
      label: "Order Type",
      render: (row) => <TableCell key="order_type">{row.order_type ?? notReported}</TableCell>,
    },
    {
      key: "side",
      label: "Transaction Type",
      sortable: true,
      sortValue: (row) => row.side,
      render: (row) => <TableCell key="side">{row.side}</TableCell>,
    },
    {
      key: "quantity",
      label: "Quantity",
      align: "right",
      sortable: true,
      sortValue: (row) => row.quantity,
      render: (row) => <QuantityCell key="quantity" value={row.quantity} />,
    },
    {
      key: "filled_quantity",
      label: "Filled Quantity",
      align: "right",
      render: (row) => <QuantityCell key="filled_quantity" value={row.filled_quantity} />,
    },
    {
      key: "price",
      label: "Price",
      align: "right",
      render: (row) => <PriceCell key="price" value={row.price} />,
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <TableCell key="status">
          <PositionBadge status={row.status} />
        </TableCell>
      ),
    },
    {
      key: "created_time",
      label: "Created Time",
      render: (row) => <TableCell key="created_time">{row.created_time ?? notReported}</TableCell>,
    },
    {
      key: "updated_time",
      label: "Updated Time",
      render: (row) => <TableCell key="updated_time">{row.updated_time ?? notReported}</TableCell>,
    },
  ];

  return (
    <DataTable ariaLabel="Orders" columns={columns} rows={orders} getRowKey={(row) => row.execution_id} />
  );
}
